import { useState, useEffect } from "react";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { DatePicker } from "../ui/DatePicker";
import { SearchableSelect } from "../ui/SearchableSelect";
import { Download, AlertCircle, CheckCircle } from "lucide-react";
import { enrollmentService } from "../../services/enrollmentService";
import {
  convertToCSV,
  downloadCSV,
  formatEnrollmentForCSV,
  generateTimestampedFilename,
} from "../../utils/csvExport";

interface ExportEnrollmentsDialogProps {
  isOpen: boolean;
  onClose: () => void;
  courses: { _id: string; title: string }[];
  initialFilters?: {
    status?: string;
    type?: string;
    courseId?: string;
    minWatchTime?: string;
    maxWatchTime?: string;
    paymentMethod?: string;
    fromDate?: string;
    toDate?: string;
  };
  initialSearchTerm?: string;
}

export function ExportEnrollmentsDialog({
  isOpen,
  onClose,
  courses,
  initialFilters,
  initialSearchTerm,
}: ExportEnrollmentsDialogProps) {
  const [filters, setFilters] = useState({
    status: initialFilters?.status ?? "all",
    type: initialFilters?.type ?? "all",
    courseId: initialFilters?.courseId ?? "",
    minWatchTime: initialFilters?.minWatchTime ?? "",
    maxWatchTime: initialFilters?.maxWatchTime ?? "",
    paymentMethod: initialFilters?.paymentMethod ?? "",
    fromDate: initialFilters?.fromDate ?? "",
    toDate: initialFilters?.toDate ?? "",
  });
  const [searchTerm, setSearchTerm] = useState(initialSearchTerm ?? "");

  const [recordCount, setRecordCount] = useState<number | null>(null);
  const [isLoadingCount, setIsLoadingCount] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [notification, setNotification] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);

  // Fetch record count when filters change
  useEffect(() => {
    if (isOpen) {
      fetchRecordCount();
    }
  }, [filters, searchTerm, isOpen]);

  const buildFilterParams = () => {
    const params: any = {};

    if (filters.status !== "all") {
      params.status = filters.status;
    }

    if (filters.type !== "all") {
      params.isFree = filters.type === "free";
    }

    if (filters.courseId) {
      params.courseId = filters.courseId;
    }

    if (filters.minWatchTime) {
      params.minWatchTime = parseFloat(filters.minWatchTime) * 3600;
    }

    if (filters.maxWatchTime) {
      params.maxWatchTime = parseFloat(filters.maxWatchTime) * 3600;
    }

    if (filters.fromDate) {
      params.fromDate = filters.fromDate;
    }

    if (filters.toDate) {
      params.toDate = filters.toDate;
    }

    if (filters.paymentMethod) {
      params.paymentMethod = filters.paymentMethod;
    }

    if (searchTerm) {
      params.search = searchTerm;
    }

    return params;
  };

  const fetchRecordCount = async () => {
    setIsLoadingCount(true);
    try {
      const params = buildFilterParams();
      const response = await enrollmentService.getEnrollmentCount(params);
      setRecordCount(response.total || 0);
    } catch (error) {
      console.error("Failed to fetch record count:", error);
      setRecordCount(0);
    } finally {
      setIsLoadingCount(false);
    }
  };

  const handleExport = async () => {
    setIsExporting(true);
    setNotification(null);

    try {
      const params = buildFilterParams();
      const response = await enrollmentService.exportEnrollments(params);
      const enrollments = response.enrollments || [];

      if (enrollments.length === 0) {
        setNotification({
          type: "error",
          message: "No records found matching the selected filters.",
        });
        setIsExporting(false);
        return;
      }

      // Format data for CSV
      const formattedData = enrollments.map(formatEnrollmentForCSV);

      // Define CSV headers
      const headers = [
        { key: "enrollmentId", label: "Enrollment ID" },
        { key: "studentName", label: "Student Name" },
        { key: "studentEmail", label: "Student Email" },
        { key: "courseName", label: "Course Name" },
        { key: "enrollmentType", label: "Enrollment Type" },
        { key: "status", label: "Status" },
        { key: "enrollmentDate", label: "Enrollment Date" },
        { key: "expiryDate", label: "Expiry Date" },
        { key: "bandwidthConsumption", label: "Bandwidth Consumption" },
        { key: "amountPaid", label: "Amount Paid" },
        { key: "currency", label: "Currency" },
        { key: "paymentMethod", label: "Payment Method" },
        { key: "progress", label: "Progress" },
      ];

      // Convert to CSV
      const csvContent = convertToCSV(formattedData, headers);

      // Generate filename and download
      const filename = generateTimestampedFilename("enrollments");
      downloadCSV(csvContent, filename);

      setNotification({
        type: "success",
        message: `Successfully exported ${enrollments.length} enrollment(s) to ${filename}`,
      });

      // Close dialog after 2 seconds
      setTimeout(() => {
        onClose();
        setNotification(null);
      }, 2000);
    } catch (error: any) {
      console.error("Export failed:", error);
      setNotification({
        type: "error",
        message:
          error.message || "Failed to export enrollments. Please try again.",
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleReset = () => {
    setFilters({
      status: "all",
      type: "all",
      courseId: "",
      minWatchTime: "",
      maxWatchTime: "",
      paymentMethod: "",
      fromDate: "",
      toDate: "",
    });
    setSearchTerm("");
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Export Enrollments to CSV">
      <div className="space-y-6 py-2">
        {/* Date Range Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Enrollment Date Range
          </label>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <DatePicker
                value={filters.fromDate}
                onChange={(val) => setFilters({ ...filters, fromDate: val })}
                placeholder="From date"
                max={filters.toDate || undefined}
              />
            </div>
            <span className="text-gray-400">to</span>
            <div className="flex-1">
              <DatePicker
                value={filters.toDate}
                onChange={(val) => setFilters({ ...filters, toDate: val })}
                placeholder="To date"
                min={filters.fromDate || undefined}
              />
            </div>
          </div>
        </div>

        {/* Search Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Search (optional)
          </label>
          <Input
            type="text"
            placeholder="Search by student name, email, or course..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-9 text-sm"
          />
        </div>

        {/* Course Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Filter by Course
          </label>
          <SearchableSelect
            options={courses.map((c) => ({ value: c._id, label: c.title }))}
            value={filters.courseId}
            onChange={(val) => setFilters({ ...filters, courseId: val })}
            placeholder="Select a course..."
          />
        </div>

        {/* Status Filter */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">Status</label>
          <div className="grid grid-cols-3 gap-2">
            {["all", "active", "completed", "expired", "cancelled"].map((s) => (
              <button
                key={s}
                onClick={() => setFilters({ ...filters, status: s })}
                className={`px-3 py-2 text-xs rounded-lg border transition-all ${
                  filters.status === s
                    ? "bg-[#102040] text-white border-[#102040]"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                }`}
              >
                {s.charAt(0).toUpperCase() + s.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Enrollment Type */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Enrollment Type
          </label>
          <div className="flex gap-2">
            {[
              { id: "all", label: "All" },
              { id: "paid", label: "Paid" },
              { id: "free", label: "Free" },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setFilters({ ...filters, type: t.id })}
                className={`flex-1 px-3 py-2 text-xs rounded-lg border transition-all ${
                  filters.type === t.id
                    ? "bg-[#102040] text-white border-[#102040]"
                    : "bg-white text-gray-600 border-gray-200 hover:border-gray-300"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {/* Bandwidth Consumption */}
        <div className="space-y-2">
          <label className="text-sm font-medium text-gray-700">
            Bandwidth Consumption (Hours)
          </label>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <Input
                type="number"
                placeholder="Min Hours"
                value={filters.minWatchTime}
                onChange={(e) =>
                  setFilters({ ...filters, minWatchTime: e.target.value })
                }
                className="h-9 text-sm"
              />
            </div>
            <span className="text-gray-400">to</span>
            <div className="flex-1">
              <Input
                type="number"
                placeholder="Max Hours"
                value={filters.maxWatchTime}
                onChange={(e) =>
                  setFilters({ ...filters, maxWatchTime: e.target.value })
                }
                className="h-9 text-sm"
              />
            </div>
          </div>
        </div>

        {/* Record Count Preview */}
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              Records to Export:
            </span>
            <span className="text-lg font-bold text-[#102040]">
              {isLoadingCount ? (
                <div className="animate-spin inline-block w-5 h-5 border-2 border-current border-t-transparent rounded-full" />
              ) : recordCount !== null ? (
                recordCount.toLocaleString()
              ) : (
                "—"
              )}
            </span>
          </div>
        </div>

        {/* Notification */}
        {notification && (
          <div
            className={`flex items-center gap-2 p-3 rounded-lg ${
              notification.type === "success"
                ? "bg-green-50 text-green-700 border border-green-200"
                : "bg-red-50 text-red-700 border border-red-200"
            }`}
          >
            {notification.type === "success" ? (
              <CheckCircle size={18} />
            ) : (
              <AlertCircle size={18} />
            )}
            <span className="text-sm">{notification.message}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-between gap-3 pt-6 border-t border-gray-100">
          <Button
            variant="outline"
            onClick={handleReset}
            className="px-6 text-red-600 hover:bg-red-50 hover:border-red-100"
            disabled={isExporting}
          >
            Reset Filters
          </Button>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="px-6"
              disabled={isExporting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleExport}
              className="px-8 bg-[#102040] hover:bg-[#081a30] flex items-center gap-2"
              disabled={isExporting || recordCount === 0}
            >
              {isExporting ? (
                <>
                  <div className="animate-spin inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full" />
                  Exporting...
                </>
              ) : (
                <>
                  <Download size={16} />
                  Export CSV
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
